import { SLOTS, createItem, itemName } from './items';

// The starting party. Stats use the MM3 attributes:
// might, intellect, personality, endurance, speed, accuracy, luck.
const STARTING_PARTY = [
    {
        name: 'Thorin', race: 'Dwarf', className: 'Knight',
        portrait: './character_art/male_dwarf_knight.jpg',
        stats: { might: 18, intellect: 8, personality: 9, endurance: 17, speed: 10, accuracy: 13, luck: 9 },
        maxHp: 30, maxSp: 0,
        gear: { weapon: 'Long Sword', armor: 'Chain Mail' },
        spells: [],
    },
    {
        name: 'Freya', race: 'Human', className: 'Barbarian',
        portrait: './character_art/female_human_barbarian.jpg',
        stats: { might: 20, intellect: 7, personality: 8, endurance: 18, speed: 12, accuracy: 11, luck: 10 },
        maxHp: 34, maxSp: 0,
        gear: { weapon: 'Battle Axe', armor: 'Leather Armour' },
        spells: [],
    },
    {
        name: 'Aldric', race: 'Human', className: 'Ranger',
        portrait: './character_art/male_human_ranger.jpg',
        stats: { might: 15, intellect: 11, personality: 9, endurance: 13, speed: 14, accuracy: 15, luck: 10 },
        // Rangers know the Cleric's spells, with a smaller spell point pool
        maxHp: 22, maxSp: 4,
        gear: { weapon: 'Short Sword', missile: 'Long Bow', armor: 'Leather Armour', helm: 'Cap' },
        spells: ['firstAid', 'flyingFist'],
    },
    {
        name: 'Vesper', race: 'Human', className: 'Robber',
        portrait: './character_art/female_human_rogue.jpg',
        stats: { might: 12, intellect: 10, personality: 8, endurance: 12, speed: 17, accuracy: 16, luck: 15 },
        maxHp: 18, maxSp: 0,
        gear: { weapon: 'Short Sword', missile: 'Crossbow', armor: 'Robes', cloak: 'Cloak' },
        spells: [],
    },
    {
        name: 'Seraphina', race: 'Human', className: 'Cleric',
        portrait: './character_art/female_human_cleric.jpg',
        stats: { might: 12, intellect: 9, personality: 18, endurance: 12, speed: 11, accuracy: 10, luck: 11 },
        maxHp: 18, maxSp: 9,
        gear: { weapon: 'Mace', missile: 'Sling', armor: 'Leather Armour', helm: 'Cap' },
        spells: ['firstAid', 'flyingFist'],
    },
    {
        name: 'Malachar', race: 'Human', className: 'Sorcerer',
        portrait: './character_art/male_human_sorceror.jpg',
        stats: { might: 8, intellect: 19, personality: 9, endurance: 10, speed: 13, accuracy: 12, luck: 11 },
        maxHp: 10, maxSp: 10,
        gear: { weapon: 'Dagger' },
        spells: ['magicArrow', 'energyBlast'],
    },
];

// What a character can carry beyond what they are wearing
export const BACKPACK_SIZE = 12;

const BARE_HANDS = { name: 'Fists', dice: [1, 2], toHit: 0, damageBonus: 0 };

// Work out the numbers combat uses (weapon, missile, armour class and
// attributes) from whatever the character has equipped.
export function recalculate(character) {
    const worn = SLOTS.map((slot) => character.equipment[slot]).filter(Boolean);
    const stats = { ...character.baseStats };
    let armor = 0;

    worn.forEach((item) => {
        armor += (item.ac || 0) + (item.acBonus || 0);
        if (item.statBonus) {
            stats[item.statBonus.stat] = (stats[item.statBonus.stat] || 0) + item.statBonus.amount;
        }
    });

    const weapon = character.equipment.weapon;
    const missile = character.equipment.missile;
    return {
        ...character,
        stats,
        armor,
        weapon: weapon
            ? { name: itemName(weapon), dice: weapon.dice, toHit: weapon.toHit, damageBonus: weapon.damageBonus }
            : BARE_HANDS,
        missile: missile
            ? { name: itemName(missile), dice: missile.dice, toHit: missile.toHit,
                damageBonus: missile.damageBonus, projectile: missile.projectile }
            : null,
    };
}

// Plain, unenchanted starting kit
function startingEquipment(gear) {
    const equipment = {};
    SLOTS.forEach((slot) => { equipment[slot] = null; });
    Object.entries(gear).forEach(([slot, kind]) => {
        equipment[slot] = createItem({ slot, kind });
    });
    return equipment;
}

export function createParty() {
    return STARTING_PARTY.map((template, index) => recalculate({
        ...template,
        id: 'p' + index,
        level: 1,
        xp: 0,
        hp: template.maxHp,
        sp: template.maxSp,
        condition: 'good',
        hitCount: 0,
        baseStats: template.stats,
        equipment: startingEquipment(template.gear),
        backpack: [],
    }));
}

// Wear an item from the backpack, putting anything it replaces back
export function equipItem(character, itemId) {
    const item = character.backpack.find((i) => i.id === itemId);
    if (!item || item.slot === 'consumable') return character;
    const replaced = character.equipment[item.slot];
    const backpack = character.backpack.filter((i) => i.id !== itemId);
    if (replaced) backpack.push(replaced);
    return recalculate({
        ...character,
        equipment: { ...character.equipment, [item.slot]: item },
        backpack,
    });
}

// Take something off, if there is room to carry it
export function unequipSlot(character, slot) {
    const item = character.equipment[slot];
    if (!item || character.backpack.length >= BACKPACK_SIZE) return character;
    return recalculate({
        ...character,
        equipment: { ...character.equipment, [slot]: null },
        backpack: [...character.backpack, item],
    });
}

// Drink a potion: returns the character and how much it restored
export function useItem(character, itemId) {
    const item = character.backpack.find((i) => i.id === itemId);
    if (!item || !item.restores) return { character, restored: 0, item: null };
    if (character.condition === 'dead') return { character, restored: 0, item };

    const backpack = character.backpack.filter((i) => i.id !== itemId);
    if (item.restores === 'sp') {
        if (character.maxSp === 0) return { character, restored: 0, item, wasted: true };
        const restored = Math.min(item.amount, character.maxSp - character.sp);
        return { character: { ...character, sp: character.sp + restored, backpack }, restored, item };
    }
    const restored = Math.min(item.amount, character.maxHp - character.hp);
    const healed = { ...character, hp: character.hp + restored, backpack };
    if (healed.hp > 0) healed.condition = 'good';
    return { character: healed, restored, item };
}

export function dropItem(character, itemId) {
    if (!character.backpack.some((i) => i.id === itemId)) return character;
    return { ...character, backpack: character.backpack.filter((i) => i.id !== itemId) };
}

// Hand an item from one character to another, if the receiver has room
export function transferItem(party, fromIndex, toIndex, itemId) {
    const from = party[fromIndex];
    const to = party[toIndex];
    if (!from || !to || fromIndex === toIndex) return { party, item: null, full: false };
    const item = from.backpack.find((i) => i.id === itemId);
    if (!item) return { party, item: null, full: false };
    if (to.backpack.length >= BACKPACK_SIZE) return { party, item, full: true };
    return {
        party: party.map((c, i) => {
            if (i === fromIndex) return { ...c, backpack: c.backpack.filter((b) => b.id !== itemId) };
            if (i === toIndex) return { ...c, backpack: [...c.backpack, item] };
            return c;
        }),
        item,
        full: false,
    };
}

// `wanted` asks for one character in particular — a purchase goes into the
// buyer's own pack — otherwise the first with room takes it.
export function giveItem(party, item, wanted) {
    const room = (c) => c && c.backpack.length < BACKPACK_SIZE;
    const index = wanted === undefined
        ? party.findIndex(room)
        : (room(party[wanted]) ? wanted : -1);
    if (index === -1) return { party, taker: null };
    const taker = party[index];
    const updated = party.map((c, i) => (i === index ? { ...c, backpack: [...c.backpack, item] } : c));
    return { party: updated, taker };
}

// Resting restores every living character to full health and spell points
export function restParty(party) {
    return party.map((c) => (c.condition === 'dead' ? c : { ...c, hp: c.maxHp, sp: c.maxSp, condition: 'good' }));
}
